import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchMerchants, Merchant } from '../../services/merchant';
import { UserRole } from '../../types/auth';

/* =========================
   Types
========================= */

export interface MerchantFilters {
    group: string;
    kycStatus: 'all' | 'Verified' | 'NotVerified' | 'Not Verified' | 'KycPending';
    dateRange: {
        from?: string;
        to?: string;
    };
}

export interface MerchantState {
    merchants: Merchant[];
    total: number;
    loading: boolean;

    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    searchQuery: string;
    filters: MerchantFilters;

    hasFetched: boolean;
    error?: string;
}

/* =========================
   Initial State
========================= */

const initialState: MerchantState = {
    merchants: [],
    total: 0,
    loading: false,

    page: 1,
    pageSize: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    searchQuery: '',
    filters: {
        group: '',
        kycStatus: 'KycPending',
        dateRange: { from: undefined, to: undefined },
    },

    hasFetched: false,
};

/* =========================
   Async Thunk (✅ FIXED)
========================= */

export const fetchKycPendingMerchantsThunk = createAsyncThunk(
    'merchantKYC/fetchMerchants', // ✅ unique action type
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState() as {
                merchantKYC: MerchantState;
                auth: { token: string; user: any };
            };

            const { token, user } = state.auth;
            const {
                page,
                pageSize,
                sortBy,
                sortOrder,
                searchQuery,
                filters,
            } = state.merchantKYC;

            const isCleverUser = user?.role === UserRole.CLEVERPE_USER;

            // ✅ FORCE KYC PENDING FILTER (IMPORTANT)
            const finalFilters: MerchantFilters = {
                ...filters,
                kycStatus: 'KycPending',
            };

            const hasSearchOrFilters =
                searchQuery.trim() !== '' ||
                !!finalFilters.group ||
                !!finalFilters.dateRange?.from ||
                !!finalFilters.dateRange?.to;


            if (isCleverUser && !hasSearchOrFilters) {
                return { merchants: [], total: 0, page, pageSize };
            }

            const res = await fetchMerchants({
                page,
                pageSize,
                sortField: sortBy,
                sortOrder,
                search: searchQuery,
                token,
                filters: finalFilters, // ✅ USE FORCED FILTERS
            });

            return {
                merchants: res.merchants,
                total: res.totalMatch,
                page: res.pageNo,
                pageSize: res.pageSize,
            };
        } catch (err: any) {
            return rejectWithValue(err.message);
        }
    }
);


/* =========================
   Slice
========================= */

const merchantKYCSlice = createSlice({
    name: 'merchantKYC', // ✅ UNIQUE SLICE NAME
    initialState,
    reducers: {
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        setPageSize: (state, action: PayloadAction<number>) => {
            state.pageSize = action.payload;
        },
        setSortBy: (state, action: PayloadAction<string>) => {
            state.sortBy = action.payload;
        },
        setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
            state.sortOrder = action.payload;
        },
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
        },
        setFilters: (state, action: PayloadAction<MerchantFilters>) => {
            state.filters = action.payload;
            state.page = 1;
        },
        resetFilters: (state) => {
            state.filters = initialState.filters;
            state.page = 1;
        },
        setHasFetched: (state, action: PayloadAction<boolean>) => {
            state.hasFetched = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchKycPendingMerchantsThunk.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(fetchKycPendingMerchantsThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.merchants = action.payload.merchants;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.pageSize = action.payload.pageSize;
                state.hasFetched = true;
            })
            .addCase(fetchKycPendingMerchantsThunk.rejected, (state, action) => {
                state.loading = false;
                state.merchants = [];
                state.total = 0;
                state.error = action.payload as string;
            });
    },
});

/* =========================
   Exports
========================= */

export const {
    setPage,
    setPageSize,
    setSortBy,
    setSortOrder,
    setSearchQuery,
    setFilters,
    resetFilters,
    setHasFetched,
} = merchantKYCSlice.actions;

export default merchantKYCSlice.reducer;
